import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPoReconciliation,
  attachPoToInvoice,
  detachPoFromInvoice,
  listCandidateInvoices,
  setInvoiceLineMapping,
  clearInvoiceLineMapping,
  pairingErrorMessage,
  type CandidateInvoice,
  type AttachedInvoice,
  type ReconReport,
  type ReconLine,
  type ReconLineStatus,
  type RecostedClientInvoiceLine,
} from '../../lib/reconciliationApi';
import { invoiceCausedOverLines } from './overBilling';
import { CandidateInvoiceCells } from './CandidateInvoiceRow';
import { currencyPrefix, formatCurrency } from '../../utils/priceUtils';
import { ValueChange } from '../../components/ui/ValueChange';
import { costInputText } from '../clientInvoice/lineCostCurrency';
import { formatDate } from '../../utils/dateFormat';
// A quantity on the 6-dp grid: a bill read through its case (2 x 6.81 KG) is 13.62, never its float dust (D170.1).
import { formatQty } from '../../utils/unitConversions';

const BORDER = '#E4E6E5';

// D104.30: the one formatter. Exact figures as the report states them (no ceil).
// A null currency prints no label: a currency is never invented.
const money = (n: number | null | undefined, currency: string | null) => formatCurrency(n, currency, { ceil: false });
// A client-invoice cost as its Cost cell states it (exact, D109.11), labelled with that invoice's currency (D171.8).
const costText = (n: number | null, currency: string | null) =>
  n == null ? '' : `${n < 0 ? '-' : ''}${currencyPrefix(currency)}${costInputText(Math.abs(n))}`;

const STATUS_STYLE: Record<ReconLineStatus, string> = {
  match: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  short: 'bg-amber-50 text-amber-700 border-amber-200',
  over: 'bg-amber-50 text-amber-700 border-amber-200',
  missing: 'bg-rose-50 text-rose-700 border-rose-200',
  // Ordered and billed in different units, so there is no short/over verdict to
  // give — the two quantities do not describe the same amount of anything.
  // Slate rather than a warning colour: nothing is known to be wrong yet.
  unit_mismatch: 'bg-slate-100 text-slate-700 border-slate-300',
  // Same reasoning as unit_mismatch: the ordered or billed quantity could not
  // be read at all, so there is no short/over/match verdict to give either.
  data_missing: 'bg-slate-100 text-slate-700 border-slate-300',
  // D81: removed from the order after the PO was sent, and not billed — fine.
  removed: 'bg-slate-50 text-slate-500 border-slate-200',
};

const STATUS_LABEL: Record<ReconLineStatus, string> = {
  match: 'match',
  short: 'short',
  over: 'over',
  missing: 'missing',
  unit_mismatch: 'unit?',
  data_missing: 'qty?',
  removed: 'removed',
};

interface Props {
  poId: string;
  poNumber: string;
  /** Opens a vendor invoice. Given, every invoice number in the panel is a link; omitted, it is plain text. */
  onOpenInvoice?: (vendorInvoiceId: string) => void;
  /** PO-screen edit permission: without it no Attach, Remove, Pair with or Unpair is shown (D171.5). */
  canEdit?: boolean;
}

/** Whether an attached invoice costs a client invoice yet: only one confirmed to Inventory does (costApplied). */
const COST_APPLIED_TIP = 'Confirmed to Inventory: its prices now cost the client invoice lines traced to this PO.';
const COST_PENDING_TIP =
  'Attached and compared here, but not confirmed to Inventory yet, so it does not change any client invoice cost. Open it and press Confirm & sync.';

/** An invoice number, as a link to the invoice when the page can open it. */
const InvoiceNumber: React.FC<{ id: string; number: string; onOpen?: (id: string) => void; className?: string }> = ({ id, number, onOpen, className = 'font-mono' }) =>
  onOpen ? (
    <button type="button" onClick={() => onOpen(id)} title="Open this vendor invoice" className={`${className} text-primary-600 underline-offset-2 hover:underline`}>
      {number}
    </button>
  ) : (
    <span className={className}>{number}</span>
  );

/**
 * PO ↔ Vendor Invoice reconciliation (REC): compares what was ordered on this PO against what
 * vendors actually billed across the attached invoices, line by line. Lazy-loads on first
 * expand so the (heavier) reconciliation query doesn't run on every PO detail view.
 */
export const ReconciliationPanel: React.FC<Props> = ({ poId, poNumber, onOpenInvoice, canEdit = false }) => {
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<ReconReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Attach-invoice picker (D157.2): null = not loaded yet, or the load failed.
  const [invoices, setInvoices] = useState<CandidateInvoice[] | null>(null);
  const [invoicesFailed, setInvoicesFailed] = useState(false);
  const [picker, setPicker] = useState('');
  const [busy, setBusy] = useState(false);
  const [overWarning, setOverWarning] = useState<{ invoiceId: string; lines: { itemName: string; billedQty: number; orderedQty: number }[] } | null>(null);
  // A refused Pair with / Unpair, worded from its code (D171.5).
  const [pairError, setPairError] = useState<string | null>(null);
  // D171.8: the client-invoice lines whose cost the last attach, detach, pin or unpin moved; the next one replaces it.
  const [recosted, setRecosted] = useState<RecostedClientInvoiceLine[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await getPoReconciliation(poId));
    } catch (e) {
      setError(pairingErrorMessage(e, 'The reconciliation could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [poId]);

  // The server leaves out expenses (D95) and what is already on this PO.
  const loadInvoices = useCallback(async () => {
    setInvoicesFailed(false);
    try {
      setInvoices(await listCandidateInvoices(poId));
    } catch {
      setInvoices(null);
      setInvoicesFailed(true);
    }
  }, [poId]);

  // D114.2: one automatic attempt per PO. A failed load leaves `report` null and
  // flips `loading` back to false, which used to satisfy this guard again and
  // refetch without bound while the server kept failing. After a failure the
  // Retry button is the only way to ask again.
  const attempted = useRef(false);
  useEffect(() => {
    attempted.current = false;
  }, [poId]);
  useEffect(() => {
    if (!open || report || loading || attempted.current) return;
    attempted.current = true;
    load();
    loadInvoices();
  }, [open, report, loading, load, loadInvoices]);

  const retry = () => {
    load();
    if (!invoices) loadInvoices();
  };

  // D164: the server's list, not the invoices with a paired line: an attached bill whose lines
  // paired with nothing used to vanish (out of the picker, in no count, with no Remove).
  const attachedInvoiceIds = useMemo(() => new Set((report?.attachedInvoices || []).map((a) => a.vendorInvoiceId)), [report]);
  // By id, so a matched line can say whether its invoice is costing anything yet.
  const attachedById = useMemo(() => new Map((report?.attachedInvoices || []).map((a) => [a.vendorInvoiceId, a] as [string, AttachedInvoice])), [report]);
  const notCostingYet = (report?.attachedInvoices || []).filter((a) => !a.costApplied);
  // Invoices the match rule pairs with this PO that nobody has attached: until one is, it reconciles nothing and costs nothing.
  const unattachedMatches = (invoices || []).filter((v) => v.matches);

  // After a write that went through: the report again, and the lines this invoice now pushes over its order.
  // A failed read is the load's failure (with Retry), never the write's: the write is done.
  const reloadAfterWrite = async (invoiceId: string, before: ReconLine[]) => {
    try {
      const next = await getPoReconciliation(poId);
      setReport(next);
      setError(null);
      const overLines = invoiceCausedOverLines(before, next.lines, invoiceId);
      setOverWarning(overLines.length ? { invoiceId, lines: overLines } : null);
    } catch (e) {
      setError(pairingErrorMessage(e, 'The reconciliation could not be loaded.'));
    }
  };

  const doAttach = async () => {
    if (!picker) return;
    setBusy(true);
    const attachedId = picker;
    const before = report?.lines || [];
    try {
      setRecosted(await attachPoToInvoice(attachedId, poId, poNumber));
    } catch (e) {
      alert(pairingErrorMessage(e, 'The invoice could not be attached. Try again.'));
      setBusy(false);
      return;
    }
    setPicker('');
    loadInvoices();
    await reloadAfterWrite(attachedId, before);
    setBusy(false);
  };

  const doDetach = async (vendorInvoiceId: string) => {
    if (!window.confirm('Detach this invoice from the PO?')) return;
    setBusy(true);
    try {
      setRecosted(await detachPoFromInvoice(vendorInvoiceId, poId));
      loadInvoices();
      await load();
      setOverWarning(null);
    } catch (e) {
      alert(pairingErrorMessage(e, 'The invoice could not be removed from this PO. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  // D171.5: one bill line pinned to (or unpinned from) one line of this PO; the report is read again and,
  // as after an attach, a line the bill now pushes over its order is named.
  const changePairing = async (vendorInvoiceId: string, write: () => Promise<RecostedClientInvoiceLine[]>, fallback: string) => {
    setBusy(true);
    setPairError(null);
    const before = report?.lines || [];
    try {
      setRecosted(await write());
    } catch (e) {
      setPairError(pairingErrorMessage(e, fallback));
      setBusy(false);
      return;
    }
    await reloadAfterWrite(vendorInvoiceId, before);
    setBusy(false);
  };
  const doPair = (vendorInvoiceId: string, invoiceLineIndex: number, poLineIndex: number) =>
    changePairing(vendorInvoiceId, () => setInvoiceLineMapping(vendorInvoiceId, poId, invoiceLineIndex, poLineIndex), 'The line could not be paired. Try again.');
  const doUnpair = (vendorInvoiceId: string, invoiceLineIndex: number) =>
    changePairing(vendorInvoiceId, () => clearInvoiceLineMapping(vendorInvoiceId, poId, invoiceLineIndex), 'The pairing could not be undone. Try again.');
  // Lines a bill line may be paired with: every live line of this PO, never one removed from the order.
  const pairableLines = (report?.lines || []).filter((l) => !l.removed);

  const t = report?.totals;
  // D157.2: the ordered money is in the PO's own currency, as the report states it; none, no label.
  const currency = report?.po?.currency || null;
  // D82: on a PO priced before VAT, each line's ordered and billed money is
  // before VAT while a matched invoice's figures are as printed. A PO stored
  // before D82 is compared as printed and says nothing.
  const beforeVat = report?.po?.priceBasis === 'before_vat';
  const basis = beforeVat ? ' before VAT' : '';

  return (
    <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: BORDER }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 border-b text-sm font-medium text-slate-900"
        style={{ borderColor: BORDER }}
      >
        <span>Invoice Reconciliation {report ? `— billed ${money(t?.billedTotal, currency)} of ${money(t?.orderedTotal, currency)} ordered${basis}` : ''}</span>
        <span className="text-slate-400 text-xs">{open ? '▲ hide' : '▼ show'}</span>
      </button>

      {open && (
        <div className="p-4">
          {loading && <div className="text-[13px] text-slate-500 py-4">Loading reconciliation…</div>}
          {error && !loading && (
            <div className="flex items-center gap-3 text-[13px] text-rose-600 py-2">
              <span>{error}</span>
              <button
                type="button"
                onClick={retry}
                className="h-8 px-3 rounded-md border border-rose-300 bg-white text-rose-700 text-xs font-semibold hover:bg-rose-50"
              >
                Retry
              </button>
            </div>
          )}

          {report && !loading && (
            <>
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                <span className="px-2 py-1 rounded border border-slate-200 bg-slate-50">Ordered{basis}: <b>{money(t?.orderedTotal, currency)}</b></span>
                <span className="px-2 py-1 rounded border border-slate-200 bg-slate-50">Billed{basis}: <b>{money(t?.billedTotal, currency)}</b></span>
                {attachedInvoiceIds.size === 0 ? (
                  <span className="px-2 py-1 rounded border border-slate-200 bg-slate-50 text-slate-600">Not invoiced yet</span>
                ) : (
                  <>
                <span className="px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-700">Match: {t?.linesMatch ?? 0}</span>
                <span className="px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-700">Short: {t?.linesShort ?? 0}</span>
                <span className="px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-700">Over: {t?.linesOver ?? 0}</span>
                <span className="px-2 py-1 rounded border border-rose-200 bg-rose-50 text-rose-700">Missing: {t?.linesMissing ?? 0}</span>
                  </>
                )}
                <span className="px-2 py-1 rounded border border-slate-200 bg-slate-50">Price alerts: {t?.priceAlerts ?? 0}</span>
                <span className="px-2 py-1 rounded border border-slate-200 bg-slate-50">Invoices attached: <b>{attachedInvoiceIds.size}</b></span>
              </div>

              {overWarning && (
                <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <b>Over-billed:</b> this invoice pushed{' '}
                      {overWarning.lines.map((l, i) => (
                        <span key={i}>
                          {i > 0 ? '; ' : ''}
                          <b>{l.itemName}</b> to {formatQty(l.billedQty)} of {formatQty(l.orderedQty)} ordered
                        </span>
                      ))}
                      .
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => doDetach(overWarning.invoiceId)}
                          disabled={busy}
                          className="px-2 py-1 rounded bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 disabled:opacity-50"
                        >
                          Remove this invoice
                        </button>
                      )}
                      <button type="button" onClick={() => setOverWarning(null)} className="text-amber-700 hover:text-amber-900" title="Dismiss">✕</button>
                    </div>
                  </div>
                </div>
              )}

              {recosted.length > 0 && (
                <div role="note" aria-label="Client invoice costs changed" className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <div className="mb-1">
                    This changed the cost of {recosted.length === 1 ? '1 client invoice line' : `${recosted.length} client invoice lines`}:
                  </div>
                  <ul className="space-y-0.5">
                    {recosted.map((l) => (
                      <li key={`${l.invoiceId}-${l.lineNumber}`}>
                        <span className="font-mono">{l.invoiceNumber || 'no number'}</span> line {l.lineNumber}
                        {l.itemName ? `, ${l.itemName}` : ''}: <ValueChange from={costText(l.before, l.currency)} to={costText(l.after, l.currency)} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {notCostingYet.length > 0 && (
                <div role="note" className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {notCostingYet.length === 1 ? '1 attached invoice is' : `${notCostingYet.length} attached invoices are`} not confirmed to Inventory yet, so the prices
                  billed here do not reach any client invoice's cost. Open {notCostingYet.length === 1 ? 'it' : 'each'} and press Confirm &amp; sync.
                </div>
              )}
              {unattachedMatches.length > 0 && (
                <div role="note" className="mb-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                  {unattachedMatches.length === 1 ? '1 vendor invoice looks' : `${unattachedMatches.length} vendor invoices look`} like {unattachedMatches.length === 1 ? 'it belongs' : 'they belong'} to this
                  PO but {unattachedMatches.length === 1 ? 'is' : 'are'} not attached. An unattached invoice is not reconciled here and does not change any client invoice cost: pick it below and press Attach.
                  A confirmed invoice tagged to this order attaches itself when this is its supplier's only PO on the order.
                </div>
              )}

              {pairError && (
                <div role="alert" className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {pairError}
                </div>
              )}

              {/* Attach invoice (D157.2): the server's candidates, matches first; a person picks what the D171.3 rule does not settle. */}
              {canEdit && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-slate-500">Attach invoice:</span>
                  <button
                    type="button"
                    onClick={doAttach}
                    disabled={!picker || busy}
                    className="h-8 px-3 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-50"
                  >
                    Attach
                  </button>
                </div>
                {invoicesFailed ? (
                  <div className="flex items-center gap-3 text-xs text-rose-600">
                    <span>The vendor invoices could not be loaded.</span>
                    <button
                      type="button"
                      onClick={loadInvoices}
                      className="h-7 px-3 rounded-md border border-rose-300 bg-white text-rose-700 font-semibold hover:bg-rose-50"
                    >
                      Retry
                    </button>
                  </div>
                ) : !invoices ? (
                  <div className="text-xs text-slate-500">Loading vendor invoices…</div>
                ) : invoices.length === 0 ? (
                  <div className="text-xs text-slate-400">No vendor invoices to attach.</div>
                ) : (
                  <div className="max-h-56 overflow-auto rounded border border-slate-200 divide-y divide-slate-100 text-xs">
                    {invoices.map((vi) => (
                      <label key={vi._id} className="flex items-center gap-3 px-2 py-1.5 cursor-pointer hover:bg-slate-50">
                        <input
                          type="radio"
                          name={`attach-invoice-${poId}`}
                          checked={picker === vi._id}
                          onChange={() => setPicker(vi._id)}
                        />
                        <CandidateInvoiceCells vi={vi} matchLabel="Matches this PO" />
                      </label>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Attached invoices — labeled, discoverable removal (the per-line ✕ still works too) */}
              {report.attachedInvoices.length > 0 && (
                  <div className="mb-3">
                    <div className="text-xs text-slate-500 mb-1">Attached invoices ({report.attachedInvoices.length}):</div>
                    <div className="flex flex-wrap gap-2">
                      {report.attachedInvoices.map((a) => (
                        <span key={a.vendorInvoiceId} className="inline-flex flex-wrap items-center gap-2 px-2 py-1 rounded border border-slate-200 bg-slate-50 text-xs">
                          <InvoiceNumber id={a.vendorInvoiceId} number={a.invoiceNumber || a.vendorInvoiceId.slice(-6)} onOpen={onOpenInvoice} />
                          <span className="text-slate-500">{formatDate(a.invoiceDate, 'no date')}</span>
                          <span className="text-slate-700">{a.total == null ? 'no total' : money(a.total, a.currency)}</span>
                          {a.status && <span className="rounded-full border border-slate-200 bg-white px-1.5 text-slate-500">{a.status}</span>}
                          {a.matchSource === 'auto' && (
                            <span title="Tagged to this PO's order, from this PO's supplier: it attached itself." className="text-slate-500">
                              attached automatically
                            </span>
                          )}
                          <span
                            title={a.costApplied ? COST_APPLIED_TIP : COST_PENDING_TIP}
                            className={`rounded-full border px-1.5 font-medium ${a.costApplied ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}
                          >
                            {a.costApplied ? 'confirmed, cost applied' : 'not confirmed, no cost yet'}
                          </span>
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => doDetach(a.vendorInvoiceId)}
                              disabled={busy}
                              className="text-rose-600 hover:text-rose-800 font-semibold disabled:opacity-50"
                            >
                              Remove
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
              )}

              {/* Comparison table */}
              <div className="overflow-auto border border-slate-200 rounded">
                <table className="data-table data-table-dense">
                  <thead className="bg-[#f7faf8] text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Item</th>
                      <th className="text-right px-3 py-2">Ordered (qty × price{beforeVat ? ', before VAT' : ''})</th>
                      <th className="text-right px-3 py-2">Billed (qty × price{beforeVat ? ', before VAT' : ''})</th>
                      <th className="text-center px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2">Price Δ</th>
                      <th className="text-left px-3 py-2">Invoices{beforeVat ? ' (as printed)' : ''} / Alerts</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.lines.map((line: ReconLine, i) => (
                      <tr key={i} className="align-top">
                        <td className="px-3 py-2 text-slate-800">{line.itemName || `Line ${line.poLineIndex + 1}`}</td>
                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">
                          {formatQty(line.orderedQty)} {line.orderedUom} × {money(line.orderedUnitPrice, currency)}
                          <div className="text-slate-400">{money(line.orderedTotal, currency)}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">
                          {/* Billed money is labelled with the INVOICE's currency, not
                              the PO's. These used to share one `currency` variable, so
                              a US$ figure printed under a TT$ heading and looked like a
                              bargain (USC-BUG-0008). Falls back to the PO's only when
                              the server could not name one. */}
                          {formatQty(line.billedQty)} × {money(line.billedUnitPrice, line.billedCurrency || currency)}
                          <div className="text-slate-400">{money(line.billedTotal, line.billedCurrency || currency)}</div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-medium ${line.status === 'missing' && attachedInvoiceIds.size === 0 ? STATUS_STYLE.removed : STATUS_STYLE[line.status]}`}>
                            {line.status === 'missing' && attachedInvoiceIds.size === 0 ? 'not invoiced' : STATUS_LABEL[line.status] ?? line.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {line.priceStatus === 'currency_mismatch' ? (
                            /* No percentage here on purpose: a delta between two
                               currencies is not a number about anything. Slate,
                               matching the unit_mismatch badge — nothing is known
                               to be wrong yet, it is not yet comparable. Emerald
                               (the else-branch below) would have read as a price
                               CUT on an invoice that may be 6.5x over.
                               (USC-BUG-0008.) */
                            <span
                              className="text-slate-500"
                              title={`Ordered in ${currency || 'an unstated currency'}, billed in ${line.billedCurrency || 'another currency'} — not comparable.`}
                            >
                              currency?
                            </span>
                          ) : line.priceStatus === 'data_missing' ? (
                            /* The ordered or billed price could not be read at all —
                               a 0%/$0 delta here would read as "no change" on a
                               price nobody actually verified. */
                            <span className="text-slate-500" title="The ordered or billed price could not be read — not comparable.">
                              price?
                            </span>
                          ) : line.priceStatus === 'match' ? (
                            <span className="text-slate-400">—</span>
                          ) : (
                            <span className={line.priceStatus === 'increase' ? 'text-rose-600' : 'text-emerald-600'}>
                              {line.priceDeltaPct > 0 ? '+' : ''}{line.priceDeltaPct}% ({line.priceDeltaAmount > 0 ? '+' : ''}{money(line.priceDeltaAmount, currency)})
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {line.matchedInvoices.map((m, k) => (
                            <div key={k} className="flex items-center gap-1">
                              <InvoiceNumber id={m.vendorInvoiceId} number={m.invoiceNumber || m.vendorInvoiceId.slice(-6)} onOpen={onOpenInvoice} className="font-mono text-xs" />
                              {attachedById.get(m.vendorInvoiceId)?.costApplied === false && (
                                <span title={COST_PENDING_TIP} className="rounded-full border border-amber-200 bg-amber-50 px-1.5 text-xs text-amber-700">not confirmed</span>
                              )}
                              {/* Each match prints its OWN invoice's currency (USC-BUG-0008). */}
                              <span className="text-slate-400">({formatQty(m.qty)} × {money(m.unitPrice, m.currency || currency)})</span>
                              {/* D171.5: paired by hand; Unpair undoes that one pairing, never the whole bill (the ✕). */}
                              {m.matchSource === 'manual' && <span className="text-slate-500">paired by hand</span>}
                              {canEdit && m.matchSource === 'manual' && (
                                <button
                                  type="button"
                                  onClick={() => doUnpair(m.vendorInvoiceId, m.invoiceLineIndex)}
                                  disabled={busy}
                                  title="Undo this pairing: the matcher pairs this bill line again. The invoice stays attached."
                                  className="text-xs font-semibold text-primary-600 hover:text-primary-800 disabled:opacity-40"
                                >
                                  Unpair
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  type="button"
                                  onClick={() => doDetach(m.vendorInvoiceId)}
                                  disabled={busy}
                                  title="Detach this invoice"
                                  className="text-rose-500 hover:text-rose-700 disabled:opacity-40"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          {line.alerts.map((a, k) => (
                            <div
                              key={`a${k}`}
                              className={`text-xs ${a.severity === 'critical' ? 'text-rose-700 font-semibold' : a.severity === 'warn' ? 'text-amber-700' : 'text-slate-500'}`}
                            >
                              {a.message}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* D164: billed lines no PO line claims. The server always reported them; nothing showed them. */}
              {report.unmatchedInvoiceLines.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-slate-500 mb-1">Billed on an attached invoice, on no line of this PO ({report.unmatchedInvoiceLines.length}):</div>
                  <div className="rounded border border-slate-200 divide-y divide-slate-100 text-xs">
                    {report.unmatchedInvoiceLines.map((u) => (
                      <div key={`${u.vendorInvoiceId}-${u.invoiceLineIndex}`} className="px-3 py-1.5">
                        <div className="flex flex-wrap items-center gap-2 text-slate-700">
                          <InvoiceNumber id={u.vendorInvoiceId} number={u.invoiceNumber || u.vendorInvoiceId.slice(-6)} onOpen={onOpenInvoice} />
                          <span>{u.rawName}</span>
                          <span className="text-slate-400">({u.qty} {u.uom} × {money(u.unitPrice, null)})</span>
                          {/* D171.5 (USC-BUG-0083): a person pairs what the matcher could not. The value is the PO line's position. */}
                          {canEdit && pairableLines.length > 0 && (
                            <select
                              aria-label={`Pair ${u.rawName} with a line of this PO`}
                              value=""
                              disabled={busy}
                              onChange={(e) => doPair(u.vendorInvoiceId, u.invoiceLineIndex, Number(e.target.value))}
                              className="h-7 rounded border border-slate-300 bg-white px-1 text-xs text-slate-700 disabled:opacity-50"
                            >
                              <option value="" disabled>
                                Pair with…
                              </option>
                              {pairableLines.map((l) => (
                                <option key={l.poLineIndex} value={l.poLineIndex}>
                                  {l.itemName || `Line ${l.poLineIndex + 1}`}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        {u.alerts.map((a, k) => (
                          <div key={k} className={a.severity === 'warn' ? 'text-amber-700' : 'text-slate-500'}>{a.message}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReconciliationPanel;
